import { Button, Flex, Label, Text } from '@buffer-mono/popcorn'

import type { AssignableChannel } from '../../hooks'
import type { AccessLevel, ProductAccessLevels, ProductKey } from '../../types'

import { AccessLevelSelect } from './AccessLevelSelect'
import {
  ChannelPermissionsTable,
  COMMUNITY_TOOLTIP,
  ProductHeaderLabel,
  PUBLISH_TOOLTIP,
} from './ChannelPermissionsTable'

import styles from './ChannelPermissions.module.css'

import type { JSX } from 'react'

interface ChannelPermissionsSectionProps {
  channels: AssignableChannel[]
  /** Levels applied to every channel in the collapsed state. */
  globalLevels: ProductAccessLevels
  /** Per-channel levels used in the expanded ("Customized") state. */
  overrides: Map<string, ProductAccessLevels>
  isCustomized: boolean
  onGlobalChange: (product: ProductKey, level: AccessLevel) => void
  onOverrideChange: (
    channelId: string,
    product: ProductKey,
    level: AccessLevel,
  ) => void
  onCustomize: () => void
  onReset: () => void
}

/**
 * The per-product "Channel Permissions" section. Shared by the invite dialog
 * and the team-member edit dialog. Has two states:
 *
 * - **collapsed** ("Applies to all channels"): one Publish + one Community
 *   select whose values apply to every channel.
 * - **expanded** ("Customized"): a per-channel table so each channel can
 *   differ. The Customize button seeds the table from the global values;
 *   Reset collapses back.
 */
export const ChannelPermissionsSection = ({
  channels,
  globalLevels,
  overrides,
  isCustomized,
  onGlobalChange,
  onOverrideChange,
  onCustomize,
  onReset,
}: ChannelPermissionsSectionProps): JSX.Element => {
  return (
    <Flex direction="column" gap="sm" fullWidth>
      {/* Grid header: on mobile the subtitle drops to its own line under the
          label/button row; on >=sm it sits inline after the label, where the
          separating "·" is added by CSS (CORE-6243). */}
      <div className={styles.sectionHeader}>
        <Label className={styles.sectionLabel}>Channel Permissions</Label>
        <Text color="subtle" size="md" className={styles.sectionSubtitle}>
          {isCustomized ? 'Customized' : 'Applies to all channels'}
        </Text>
        {isCustomized ? (
          <Button
            variant="tertiary"
            size="small"
            className={styles.customizeButton}
            onClick={onReset}
          >
            Reset
          </Button>
        ) : (
          <Button
            variant="tertiary"
            size="small"
            className={styles.customizeButton}
            onClick={onCustomize}
          >
            Customize
          </Button>
        )}
      </div>

      {isCustomized ? (
        <ChannelPermissionsTable
          channels={channels}
          overrides={overrides}
          defaults={globalLevels}
          onChange={onOverrideChange}
        />
      ) : (
        <div className={styles.collapsedProducts}>
          <Flex direction="column" gap="2xs">
            <ProductHeaderLabel label="Publish" tooltip={PUBLISH_TOOLTIP} />
            <AccessLevelSelect
              product="publish"
              value={globalLevels.publish}
              onChange={(level): void => onGlobalChange('publish', level)}
              aria-label="Publish access for all channels"
              fullWidth
            />
          </Flex>
          <Flex direction="column" gap="2xs">
            <ProductHeaderLabel label="Community" tooltip={COMMUNITY_TOOLTIP} />
            <AccessLevelSelect
              product="comments"
              value={globalLevels.comments}
              onChange={(level): void => onGlobalChange('comments', level)}
              aria-label="Community access for all channels"
              fullWidth
            />
          </Flex>
        </div>
      )}
    </Flex>
  )
}
